import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { ShieldCheck, ShieldAlert, Loader2, KeyRound, RefreshCw } from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { MfaSetupBody, RecoveryCodesView } from '../auth/MfaScreens';

const API = API_BASE;

function errText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  return error?.message || fallback;
}

export default function MfaSecurityCard() {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // setup dialog
  const [setupOpen, setSetupOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState(null);

  // disable dialog
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // regenerate dialog
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenCode, setRegenCode] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/mfa/status`, { headers: authHeaders });
      setStatus(res.data);
    } catch (error) {
      // Non-privileged users get 403; simply hide the card.
      setStatus(error?.response?.status === 403 ? { available: false } : null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: authHeaders });
      setSetup(res.data);
      setSetupCode('');
      setNewRecoveryCodes(null);
      setSetupOpen(true);
    } catch (error) {
      toast.error(errText(error, 'Could not start MFA setup'));
    } finally {
      setBusy(false);
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup/verify`, { code: setupCode.trim() }, { headers: authHeaders });
      setNewRecoveryCodes(res.data.recovery_codes || []);
      toast.success('Two-factor authentication enabled');
    } catch (error) {
      toast.error(errText(error, 'Verification failed'));
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`,
        { password: disablePassword, code: disableCode.trim() },
        { headers: authHeaders });
      toast.success('Two-factor authentication disabled');
      setDisableOpen(false);
      setDisablePassword('');
      setDisableCode('');
      loadStatus();
    } catch (error) {
      toast.error(errText(error, 'Could not disable MFA'));
    } finally {
      setBusy(false);
    }
  };

  const doRegenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/recovery-codes/regenerate`,
        { code: regenCode.trim() }, { headers: authHeaders });
      setRegenOpen(false);
      setRegenCode('');
      setNewRecoveryCodes(res.data.recovery_codes || []);
      setSetup(null);
      setSetupOpen(true); // reuse setup dialog to show the codes
      loadStatus();
    } catch (error) {
      toast.error(errText(error, 'Could not regenerate codes'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="flex items-center gap-2 p-5 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading security settings…
        </CardContent>
      </Card>
    );
  }
  if (!status?.available) return null;

  const enabled = !!status.enabled;

  return (
    <>
      <Card className="border-[#E4E8EB] shadow-sm" data-testid="mfa-security-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-heading">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Two-factor authentication (MFA)
          </CardTitle>
          <p className="text-sm text-text-muted">
            Add a one-time code from an authenticator app to protect this privileged account.
            {status.mandatory ? ' This is required for your role.' : ''}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
            enabled ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
          }`}>
            <div className="flex items-center gap-2">
              {enabled
                ? <ShieldCheck className="h-5 w-5 text-green-700" />
                : <ShieldAlert className="h-5 w-5 text-amber-700" />}
              <div>
                <p className={`text-sm font-semibold ${enabled ? 'text-green-900' : 'text-amber-900'}`} data-testid="mfa-status-label">
                  {enabled ? 'MFA is enabled' : 'MFA is not enabled'}
                </p>
                {enabled ? (
                  <p className="text-xs text-green-800">
                    {status.recovery_codes_remaining} recovery code(s) remaining.
                  </p>
                ) : (
                  <p className="text-xs text-amber-800">You will be asked for a code at every sign-in once enabled.</p>
                )}
              </div>
            </div>
            {enabled ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setRegenOpen(true)} data-testid="mfa-regenerate-btn">
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> New recovery codes
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg border-red-200 text-red-700 hover:bg-red-50" onClick={() => setDisableOpen(true)} data-testid="mfa-disable-btn">
                  Disable
                </Button>
              </div>
            ) : (
              <Button className="rounded-lg bg-[#0F6F6A] text-white hover:bg-[#0B5E5A]" disabled={busy} onClick={startSetup} data-testid="mfa-enable-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Set up MFA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup / recovery-codes dialog */}
      <Dialog open={setupOpen} onOpenChange={(open) => { setSetupOpen(open); if (!open) { setSetup(null); setNewRecoveryCodes(null); loadStatus(); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{newRecoveryCodes ? 'Your recovery codes' : 'Set up two-factor authentication'}</DialogTitle>
            <DialogDescription>
              {newRecoveryCodes
                ? 'Save these one-time codes now — they will not be shown again.'
                : 'Scan the QR with your authenticator app, then enter the 6-digit code.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {newRecoveryCodes ? (
              <RecoveryCodesView codes={newRecoveryCodes} onDone={() => { setSetupOpen(false); setSetup(null); setNewRecoveryCodes(null); loadStatus(); }} doneLabel="I've saved them" />
            ) : setup ? (
              <MfaSetupBody setup={setup} code={setupCode} setCode={setSetupCode} loading={busy} onSubmit={verifySetup} submitLabel="Verify and enable" />
            ) : (
              <div className="flex items-center gap-2 py-6 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Preparing…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>Confirm your password and a current code to turn MFA off.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doDisable} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="mfa-disable-pw">Account password</Label>
              <Input id="mfa-disable-pw" type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} required data-testid="mfa-disable-password" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-disable-code">Authenticator or recovery code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input id="mfa-disable-code" className="pl-9" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} required data-testid="mfa-disable-code" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDisableOpen(false)} disabled={busy}>Cancel</Button>
              <Button type="submit" className="bg-red-600 text-white hover:bg-red-700" disabled={busy} data-testid="mfa-disable-confirm">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Disable MFA
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Regenerate dialog */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Generate new recovery codes</DialogTitle>
            <DialogDescription>Enter a current code. Your previous recovery codes will stop working.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doRegenerate} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="mfa-regen-code">Authenticator or recovery code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input id="mfa-regen-code" className="pl-9" value={regenCode} onChange={(e) => setRegenCode(e.target.value)} required data-testid="mfa-regen-code" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRegenOpen(false)} disabled={busy}>Cancel</Button>
              <Button type="submit" className="bg-[#0F6F6A] text-white hover:bg-[#0B5E5A]" disabled={busy} data-testid="mfa-regen-confirm">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Generate codes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
